'use client'

import { useState } from 'react'
import Link from 'next/link'
import { FaPlus, FaMinus } from 'react-icons/fa'
import Breadcrumbs from '@/app/components/Breadcrumbs'
import AppStoreLink from '@/app/components/AppStoreLink'
import InlineAndroidWaitlist from '@/app/components/InlineAndroidWaitlist'
import { FAQS, FAQ_CATEGORIES } from './faqData'

/**
 * FAQs — July 2026 editorial pass.
 *
 * Was: boxed accordion cards with icon chips, chevrons AND plus/minus,
 * pill filters, and gradient headline. Now: hairline-ruled disclosure
 * rows, quiet text-link filter, Fraunces heading. FAQ text lives in
 * ./faqData.ts, shared with the FAQPage JSON-LD in page.tsx.
 */

export default function FAQsContent() {
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [category, setCategory] = useState<string>('All')

  const toggle = (q: string) => setOpen((prev) => ({ ...prev, [q]: !prev[q] }))
  const visible = category === 'All' ? FAQS : FAQS.filter((f) => f.category === category)

  return (
    <div className="min-h-screen bg-white">
      <div className="content pt-6">
        <Breadcrumbs items={[{ label: 'FAQs' }]} />
      </div>

      <div className="content pt-8 pb-16 sm:pb-20">
        {/* Header */}
        <div className="max-w-2xl mb-10">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
            FAQs
          </p>
          <h1 className="font-display text-[clamp(1.875rem,5vw,3rem)] font-bold text-ink leading-[1.08] mb-4">
            Common questions, straight answers
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            What microplastics are, how the app scores them, and what it costs.
          </p>
        </div>

        {/* Category filter: quiet text links */}
        <nav aria-label="Filter FAQs by category" className="mb-2">
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm border-b border-ink/10 pb-4">
            {['All', ...FAQ_CATEGORIES].map((c) => (
              <li key={c}>
                <button
                  onClick={() => setCategory(c)}
                  aria-pressed={category === c}
                  className={`transition-colors ${
                    category === c
                      ? 'font-bold text-forest underline underline-offset-4'
                      : 'text-gray-500 hover:text-ink'
                  }`}
                >
                  {c}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Ruled disclosure rows */}
        <ul className="border-b border-ink/10">
          {visible.map((faq) => {
            const isOpen = !!open[faq.question]
            return (
              <li key={faq.question} className="border-t border-ink/10 first:border-t-0">
                <button
                  onClick={() => toggle(faq.question)}
                  aria-expanded={isOpen}
                  className="w-full py-5 sm:py-6 flex items-start justify-between gap-4 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-forest rounded-control"
                >
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-gray-400 mb-1">
                      {faq.category}
                    </span>
                    <span className="block text-base sm:text-lg font-bold text-ink group-hover:text-forest transition-colors leading-snug">
                      {faq.question}
                    </span>
                  </span>
                  <span
                    aria-hidden="true"
                    className="mt-1 shrink-0 text-ink/30 group-hover:text-forest transition-colors"
                  >
                    {isOpen ? <FaMinus className="w-3.5 h-3.5" /> : <FaPlus className="w-3.5 h-3.5" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="pb-6 sm:pb-7 pr-8 space-y-3">
                    {faq.answer.map((paragraph, i) => (
                      <p key={i} className="text-base sm:text-base text-gray-600 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}
              </li>
            )
          })}
        </ul>

        {/* Quiet close */}
        <div className="mt-14 sm:mt-16 text-center">
          <p className="text-base sm:text-lg text-gray-600 mb-6">
            The fastest answer is a scan of something you already own.
          </p>
          <AppStoreLink
            source="faqs_close"
            className="inline-flex items-center justify-center bg-ink text-cream px-7 py-3.5 rounded-control hover:bg-ink/90 transition-colors font-semibold min-h-[48px] pressable"
          >
            Get 3 free scans on iOS
          </AppStoreLink>
          <p className="text-sm text-gray-500 mt-4">
            Something else on your mind?{' '}
            <Link href="/contact" className="text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest">
              Contact us
            </Link>
          </p>
        </div>
      </div>

      {/* Android catch. Mounted OUTSIDE the max-w-3xl content column so the
          component gets its natural max-w-4xl width and self-centers exactly
          like it does on the homepage — nested in the narrow column its
          two-column layout was cramped (heading and button wrapped). The
          close above is iOS-only; this captures Android readers who can't act
          on the App Store link. Surface is faqs-scoped for separate attribution. */}
      <InlineAndroidWaitlist surface="faqs_close" />
    </div>
  )
}
